import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { AlertTriangle, ArrowRight, KeyRound, Loader2, PackageCheck, RotateCcw, UserMinus } from 'lucide-react';
import { OFFBOARD_REASONS, type EmployeeDetail, type EmployeeListItem, type Page } from '@skhub/contracts';
import { useToast } from '@/app/toast';
import { api, ApiError } from '@/lib/api';
import { useInvalidateInventory, useMeta } from '@/lib/queries';
import { localDate } from '@/lib/format';
import { CategoryIcon, Field, FormError, Modal } from '@/components/ui';

const today = () => localDate();

/** Offboarding toast suffix: how many return confirmations went out (or were saved to the outbox). */
const emailed = (n: { status: string }[] | undefined) => {
  const ok = (n ?? []).filter((x) => x.status === 'sent' || x.status === 'saved').length;
  const failed = (n ?? []).filter((x) => x.status === 'failed').length;
  return `${ok ? ` · ${ok} confirmation email${ok === 1 ? '' : 's'}` : ''}${failed ? ` · ${failed} email${failed === 1 ? '' : 's'} failed` : ''}`;
};

/** Refresh everything an employee change can affect: people, inventory, licences, audit. */
function useAfterPeopleChange() {
  const qc = useQueryClient();
  const invalidate = useInvalidateInventory();
  return (detail?: EmployeeDetail) => {
    if (detail) qc.setQueryData(['employee', detail.id], detail);
    void invalidate();
    void qc.invalidateQueries({ queryKey: ['licenses'] });
  };
}

type ProfileValues = {
  employeeCode: string;
  displayName: string;
  email: string;
  team: string;
  designation: string;
  managerName: string;
  managerEmail: string;
  dateOfJoining: string;
};

/** Add a new joiner, or edit an existing profile. */
export function EmployeeFormDialog({ employee, onClose, onCreated }: { employee?: EmployeeDetail; onClose: () => void; onCreated?: (id: string) => void }) {
  const meta = useMeta();
  const toast = useToast();
  const after = useAfterPeopleChange();
  const uid = useId();
  const editing = !!employee;
  const [v, setV] = useState<ProfileValues>({
    employeeCode: employee?.employeeCode ?? '',
    displayName: employee?.displayName ?? '',
    email: employee?.email ?? '',
    team: employee?.team ?? '',
    designation: employee?.designation ?? '',
    managerName: employee?.managerName ?? '',
    managerEmail: employee?.managerEmail ?? '',
    dateOfJoining: employee?.dateOfJoining ?? (editing ? '' : today()),
  });
  const set = (k: keyof ProfileValues) => (ev: { target: { value: string } }) => setV((s) => ({ ...s, [k]: ev.target.value }));
  const m = useMutation({
    mutationFn: () => {
      const { employeeCode, ...profile } = v;
      return editing
        ? api.put<EmployeeDetail>(`/employees/${employee!.id}`, { ...profile, employeeCode: employeeCode.trim(), updatedAt: employee!.updatedAt })
        : api.post<EmployeeDetail>('/employees', { employeeCode, ...profile });
    },
    onSuccess: (d) => {
      after(d);
      toast({ kind: 'success', title: editing ? 'Profile saved' : 'Employee added', detail: `${d.displayName} · ${d.employeeCode}` });
      onClose();
      if (!editing) onCreated?.(d.id);
    },
  });
  const fields = m.error instanceof ApiError ? (m.error.fields ?? {}) : {};
  // A clash on email or Employee ID usually means a returning employee: find the existing profile.
  const clashOn = !editing && m.error instanceof ApiError && m.error.status === 409 ? (fields.email ? v.email.trim() : fields.employeeCode ? v.employeeCode.trim() : '') : '';
  const existing = useQuery({
    queryKey: ['employees', 'clash', clashOn],
    queryFn: () => api.get<Page<EmployeeListItem>>('/employees', { q: clashOn, status: 'all', pageSize: 5 }),
    enabled: !!clashOn,
    select: (p) => p.items.find((x) => x.email.toLowerCase() === clashOn.toLowerCase() || x.employeeCode.toLowerCase() === clashOn.toLowerCase()) ?? null,
  });
  const navigate = useNavigate();
  const match = clashOn ? existing.data : null;
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    m.mutate();
  };
  const id = (k: string) => `${uid}-${k}`;
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={editing ? 'Edit profile' : 'Add employee'}
      description={editing ? `${employee!.displayName} · ${employee!.employeeCode}` : 'New joiners start with the Employee role and can sign in to see their own devices.'}
      width="max-w-xl"
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {match ? (
          <div role="alert" className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-line bg-surface-2 px-3.5 py-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1 text-[13px]">
              <p className="font-medium text-fg">
                {match.displayName} ({match.employeeCode}) is {match.isActive ? 'already in the register' : 'a former employee'}.
              </p>
              <p className="mt-0.5 text-fg-2">
                {match.isActive ? 'Open their profile instead of creating a duplicate.' : 'Rehiring? Reactivate their existing profile so their device history stays in one place.'}
              </p>
            </div>
            <button
              type="button"
              className={`btn btn-sm shrink-0 ${match.isActive ? 'btn-secondary' : 'btn-primary'}`}
              onClick={() => {
                onClose();
                navigate(`/employees/${match.id}`);
              }}
            >
              {match.isActive ? (
                <>
                  Open profile <ArrowRight aria-hidden className="size-3.5" />
                </>
              ) : (
                <>
                  <RotateCcw aria-hidden className="size-3.5" /> Go to reactivate
                </>
              )}
            </button>
          </div>
        ) : (
          <FormError error={m.error} fieldsInline />
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" htmlFor={id('name')} required error={fields.displayName}>
            <input id={id('name')} className="field" value={v.displayName} onChange={set('displayName')} autoComplete="off" autoFocus required />
          </Field>
          <Field label="Employee ID" htmlFor={id('code')} required error={fields.employeeCode} hint={
              editing && v.employeeCode.trim() !== employee!.employeeCode
                ? `Changes from ${employee!.employeeCode}. Devices, licences and history stay with this person.`
                : 'As issued by HR, e.g. QID0123'
            }
          >
            <input id={id('code')} className="field mono" value={v.employeeCode} onChange={set('employeeCode')} autoComplete="off" required />
          </Field>
          <Field label="Work email" htmlFor={id('email')} required error={fields.email}>
            <input id={id('email')} type="email" className="field" value={v.email} onChange={set('email')} placeholder="name@smartkargo.com" required />
          </Field>
          <Field label="Date of joining" htmlFor={id('doj')} error={fields.dateOfJoining}>
            <input id={id('doj')} type="date" className="field" value={v.dateOfJoining} onChange={set('dateOfJoining')} />
          </Field>
          <Field label="Team" htmlFor={id('team')} error={fields.team} hint="Pick an existing team to keep allocation reports consistent.">
            <input id={id('team')} className="field" value={v.team} onChange={set('team')} list={id('teams')} />
            <datalist id={id('teams')}>
              {(meta.data?.teams ?? []).map((t) => (
                <option key={t.name} value={t.name} />
              ))}
            </datalist>
          </Field>
          <Field label="Designation" htmlFor={id('desig')} error={fields.designation}>
            <input id={id('desig')} className="field" value={v.designation} onChange={set('designation')} />
          </Field>
          <Field label="Reporting manager" htmlFor={id('mgr')} error={fields.managerName}>
            <input id={id('mgr')} className="field" value={v.managerName} onChange={set('managerName')} />
          </Field>
          <Field label="Manager email" htmlFor={id('mgrmail')} error={fields.managerEmail}>
            <input id={id('mgrmail')} type="email" className="field" value={v.managerEmail} onChange={set('managerEmail')} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending || !v.displayName.trim() || !v.email.trim() || !v.employeeCode.trim()}>
            {m.isPending && <Loader2 aria-hidden className="size-4 animate-spin" />}
            {editing ? 'Save profile' : 'Add employee'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Offboarding: one confirmation returns every device to IT, releases every licence seat and ends
 * sign-in. The checklist makes the physical collection explicit before anything is recorded.
 */
export function OffboardDialog({ employee: e, onClose }: { employee: EmployeeDetail; onClose: () => void }) {
  const meta = useMeta();
  const toast = useToast();
  const after = useAfterPeopleChange();
  const uid = useId();
  const targets = (meta.data?.statuses ?? []).filter((s) => s !== meta.data?.assignedStatus && s !== meta.data?.retiredStatus);
  const [reason, setReason] = useState<(typeof OFFBOARD_REASONS)[number]>('Resigned');
  const [lastDay, setLastDay] = useState(today());
  const [returnStatus, setReturnStatus] = useState('');
  const [condition, setCondition] = useState('');
  const [releaseSeats, setReleaseSeats] = useState(true);
  const [notify, setNotify] = useState(true);
  const [collected, setCollected] = useState(e.devices.length === 0);
  const [note, setNote] = useState('');
  const target = returnStatus || meta.data?.availableStatus || targets[0] || '';
  const m = useMutation({
    mutationFn: () =>
      api.post<{ devicesReturned: number; seatsReleased: number; notifications?: { status: string }[]; employee: EmployeeDetail }>(`/employees/${e.id}/offboard`, {
        updatedAt: e.updatedAt,
        lastWorkingDay: lastDay,
        reason,
        note,
        returnStatus: target,
        returnCondition: condition || null,
        releaseSeats,
        notify,
        // Exactly what was shown and confirmed; the server refuses if anything was assigned since.
        deviceIds: e.devices.map((d) => d.id),
        seatIds: e.openSeats.map((s) => s.seatId),
      }),
    onSuccess: (r) => {
      after(r.employee);
      toast({
        kind: 'success',
        title: `${e.displayName} offboarded`,
        detail: `${r.devicesReturned} device${r.devicesReturned === 1 ? '' : 's'} returned to ${target}${r.seatsReleased ? ` · ${r.seatsReleased} licence seat${r.seatsReleased === 1 ? '' : 's'} released` : ''} · sign-in disabled${emailed(r.notifications)}`,
      });
      onClose();
    },
  });
  const id = (k: string) => `${uid}-${k}`;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Offboard employee" description={`${e.displayName} · ${e.employeeCode}${e.team ? ` · ${e.team}` : ''}`} width="max-w-2xl">
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          m.mutate();
        }}
        className="space-y-5"
      >
        <FormError error={m.error} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Reason for leaving" htmlFor={id('reason')} required>
            <select id={id('reason')} className="field" value={reason} onChange={(ev) => setReason(ev.target.value as typeof reason)}>
              {OFFBOARD_REASONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </Field>
          <Field label="Last working day" htmlFor={id('lwd')} required>
            <input id={id('lwd')} type="date" className="field" value={lastDay} onChange={(ev) => setLastDay(ev.target.value)} required />
          </Field>
        </div>

        <section aria-labelledby={id('dev-h')} className="rounded-[var(--radius-lg)] border border-line">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <h3 id={id('dev-h')} className="flex items-center gap-2 text-[13px] font-semibold text-fg">
              <PackageCheck aria-hidden className="size-4 text-fg-3" /> Devices to collect · {e.devices.length}
            </h3>
            {e.devices.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="sr-only" htmlFor={id('to')}>
                  Return devices to
                </label>
                <select id={id('to')} className="field h-8 w-auto text-xs" value={target} onChange={(ev) => setReturnStatus(ev.target.value)}>
                  {targets.map((s) => (
                    <option key={s} value={s}>
                      Return to {s}
                    </option>
                  ))}
                </select>
                <label className="sr-only" htmlFor={id('cond')}>
                  Condition on return
                </label>
                <select id={id('cond')} className="field h-8 w-auto text-xs" value={condition} onChange={(ev) => setCondition(ev.target.value)}>
                  <option value="">Condition: not assessed</option>
                  {(meta.data?.handoverConditions ?? []).map((c) => (
                    <option key={c} value={c}>
                      Condition: {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </header>
          {e.devices.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-fg-3">No devices are assigned to {e.displayName}.</p>
          ) : (
            <ul className="max-h-48 divide-y divide-line overflow-y-auto">
              {e.devices.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-4 py-2">
                  <span className="grid size-7 place-items-center rounded-[var(--radius-sm)] bg-surface-2 text-fg-3">
                    <CategoryIcon type={d.assetType} className="size-3.5" />
                  </span>
                  <span className="mono text-[13px] font-medium text-fg">{d.assetTag}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-fg-3">
                    {d.assetType} · {d.name}
                  </span>
                  {d.serialNumber && <span className="mono hidden text-2xs text-fg-3 sm:block">S/N {d.serialNumber}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        {e.openSeats.length > 0 && (
          <section className="rounded-[var(--radius-lg)] border border-line px-4 py-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-0.5 accent-[var(--brand)]" checked={releaseSeats} onChange={(ev) => setReleaseSeats(ev.target.checked)} />
              <span>
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-fg">
                  <KeyRound aria-hidden className="size-3.5 text-fg-3" /> Release {e.openSeats.length} licence seat{e.openSeats.length === 1 ? '' : 's'}
                </span>
                <span className="mt-0.5 block text-xs text-fg-3">{e.openSeats.map((s) => s.productName).join(' · ')}</span>
              </span>
            </label>
          </section>
        )}

        {e.devices.length > 0 && (
          <label className="flex items-start gap-3 rounded-[var(--radius-md)] border border-line px-3 py-2.5 text-[13px]">
            <input type="checkbox" className="mt-0.5 accent-[var(--brand)]" checked={notify} onChange={(ev) => setNotify(ev.target.checked)} />
            <span>
              <span className="font-medium text-fg">Email a return confirmation for each device</span>
              <span className="mt-0.5 block text-xs text-fg-3">
                From {meta.data?.mailFrom ?? 'the IT asset mailbox'} to {e.email || 'the employee'}
                {e.managerEmail ? `, cc ${e.managerEmail}` : ' (no manager email recorded yet)'}.
              </span>
            </span>
          </label>
        )}

        <Field label="Note" htmlFor={id('note')} hint="Optional, e.g. exit ticket or handover remarks. Saved in the audit trail.">
          <textarea id={id('note')} className="field" rows={2} maxLength={500} value={note} onChange={(ev) => setNote(ev.target.value)} />
        </Field>

        {e.devices.length > 0 && (
          <label className="flex items-start gap-3 rounded-[var(--radius-md)] bg-warn-soft px-3 py-2.5 text-[13px] text-fg">
            <input type="checkbox" className="mt-0.5 accent-[var(--brand)]" checked={collected} onChange={(ev) => setCollected(ev.target.checked)} />
            <span>
              I confirm all {e.devices.length} device{e.devices.length === 1 ? ' has' : 's have'} been physically collected from {e.displayName}.
            </span>
          </label>
        )}

        <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-line pt-4 sm:flex-row sm:items-center">
          <p className="flex items-center gap-1.5 text-xs text-fg-3">
            <AlertTriangle aria-hidden className="size-3.5 text-warn" /> Sign-in stops immediately. You can reactivate the profile later if needed.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-danger" disabled={m.isPending || !collected || !lastDay || !target}>
              {m.isPending ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <UserMinus aria-hidden className="size-4" />} Offboard {e.displayName.split(' ')[0]}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/** Rehire, or undo an offboarding made in error. Devices and seats are not restored automatically. */
export function ReactivateDialog({ employee: e, onClose }: { employee: EmployeeDetail; onClose: () => void }) {
  const toast = useToast();
  const after = useAfterPeopleChange();
  const uid = useId();
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<EmployeeDetail>(`/employees/${e.id}/reactivate`, { updatedAt: e.updatedAt, note }),
    onSuccess: (d) => {
      after(d);
      toast({ kind: 'success', title: `${e.displayName} reactivated`, detail: 'They can sign in again. Assign devices as needed.' });
      onClose();
    },
  });
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Reactivate employee" description={`${e.displayName} · ${e.employeeCode}`} width="max-w-md">
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          m.mutate();
        }}
        className="space-y-4"
      >
        <FormError error={m.error} />
        <p className="text-[13px] text-fg-2">Sign-in is restored with their previous portal role ({e.role ?? 'Employee'}). Devices and licence seats returned at offboarding are not reassigned.</p>
        <Field label="Reason" htmlFor={`${uid}-note`} hint="Optional, e.g. rehired or offboarded in error.">
          <input id={`${uid}-note`} className="field" value={note} maxLength={500} onChange={(ev) => setNote(ev.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {m.isPending && <Loader2 aria-hidden className="size-4 animate-spin" />} Reactivate
          </button>
        </div>
      </form>
    </Modal>
  );
}
